/**
 * Generic openWindow NIPE tool.
 *
 * Lets NIPE drive any registered desktop window. Per-window bespoke
 * openers (openSpecializationLab, openOnboardWizard, openVaultNote)
 * still exist for cases where the model already knew which surface
 * to invoke. This tool is the fallback: the model picks a path from
 * the enum.
 */

import type { NipeTool } from './types';

export interface OpenWindowContext {
  /** Optional. Only present when the SPA wires WindowContext into NIPE.
   * Headless fixtures pass an empty context; the handler surfaces a
   * friendly "integration not available" error. */
  openWindow?: (path: string) => Promise<{ opened: boolean; title: string }>;
}

/**
 * Catalogue of windows NIPE knows about. Keep in sync with
 * Win95Desktop.tsx registerWindow calls — adding a window here without
 * a matching registerWindow is harmless (handler returns an error),
 * but the model will hallucinate paths if entries are stale.
 */
export const NIPE_KNOWN_WINDOWS: ReadonlyArray<{
  path: string;
  title: string;
  description: string;
}> = [
  {
    path: '/documents',
    title: 'Document Library',
    description: "The user's SharePoint document library, mirrored via Microsoft Graph.",
  },
  {
    path: '/prompt-lab',
    title: 'Prompt Lab',
    description: 'Compose a multi-skill prompt against a document. Streams from OpenRouter.',
  },
  {
    path: '/skills',
    title: 'Skill Library',
    description: "List of the firm's SKILL.md files. Browse, read, or pick bundles to specialize on.",
  },
  {
    path: '/specialization-lab',
    title: 'Specialization Lab',
    description: 'Side-by-side base-model vs LoRA-specialized output. Use this to demonstrate what a specialization does.',
  },
  {
    path: '/corpus-search',
    title: 'Corpus Search',
    description: 'Vector + keyword retrieval over every document the portal has indexed.',
  },
  {
    path: '/settings',
    title: 'Settings',
    description: "User settings: BYOK OpenRouter key, workspace setup, diagnostics.",
  },
  {
    path: '/help',
    title: 'Help',
    description: "Static help topics. Prefer answering questions directly over sending the user here.",
  },
  {
    path: '/obsidian-vault',
    title: 'Obsidian Vault',
    description: 'Three-pane note editor with embedded graph and flow-editor panes.',
  },
  {
    path: '/obsidian-import',
    title: 'Obsidian Import',
    description: 'Wizard to import a folder of markdown notes into the vault.',
  },
  {
    path: '/onboard/new',
    title: 'Set up SharePoint',
    description: 'Customer onboarding wizard: tenant discovery, admin consent, provisioning.',
  },
  {
    path: '/clients',
    title: 'Client List',
    description: 'Per-client drill-down. Clicking a client routes to the Document Library.',
  },
];

const KNOWN_PATHS = NIPE_KNOWN_WINDOWS.map((w) => w.path);

export const openWindowTool: NipeTool<OpenWindowContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'openWindow',
      description:
        'Open a Repancy window for the user. Choose a path from the enum. ' +
        'Known windows: ' +
        NIPE_KNOWN_WINDOWS.map((w) => `${w.path} (${w.title}: ${w.description})`).join(' | '),
      parameters: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description:
              'Window path. One of: ' + KNOWN_PATHS.join(', '),
          },
        },
        required: ['path'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.openWindow) {
      return { error: 'openWindow integration is not available in this session.' };
    }
    const path = typeof args.path === 'string' ? args.path : '';
    if (!KNOWN_PATHS.includes(path)) {
      return {
        error: `Unknown window path "${path}". Known: ${KNOWN_PATHS.join(', ')}`,
      };
    }
    return await ctx.openWindow(path);
  },
};
